import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { COLUMN_GAP, HEIGHT_OF_ONE_HOUR } from "@miplatform/constants";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { MiIcon } from "../../MiIcon/MiIcon";

const useStyles = makeStyles()((theme) => ({
  drawerHeader: {
    justifyContent: "space-between",
    padding: 3,
    display: "flex",
  },
  drawerContent: {
    display: "flex",
    backgroundColor: theme.palette.blue.light,
    height: (HEIGHT_OF_ONE_HOUR * 3) / 4,
    padding: `4px ${COLUMN_GAP}px`,
    gap: COLUMN_GAP,
    overflowX: "scroll",
    "&::-webkit-scrollbar": { display: "none" } /* webkit */,
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
  },
  countContainer: {
    height: 20,
    width: 20,
    background: theme.palette.blue.main,
    borderRadius: "100%",
    marginRight: 10,
    marginLeft: 10,
  },
  totalCountStyles: {
    marginRight: 10,
    backgroundColor: theme.palette.red.medium,
    color: theme.palette.common.white,
    borderRadius: "100%",
    height: 20,
    width: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: theme.fontSize.small,
  },
  disabled: {
    padding: 5,
    color: theme.palette.grey.main,
  },
  noData: {
    display: "none",
  },
}));

export function Bucket({ bucket, children }) {
  const [collapse, setCollapse] = useState(bucket.collapsible);
  const { setNodeRef } = useDroppable({ id: bucket.id });

  const { classes } = useStyles();
  const theme = useTheme();

  const toggleCollapse = () => {
    if (bucket.data?.length && !bucket.collapsible) {
      return;
    }
    setCollapse((prev) => !prev);
  };

  return (
    <SortableContext
      id={bucket.id}
      items={(bucket?.data || []).map((card) => ({
        id: card.uuid,
      }))}
      strategy={rectSortingStrategy}
    >
      <Box overflow="hidden" width="100%">
        <Box
          data-testid="header"
          onClick={toggleCollapse}
          className={
            bucket.data?.length ? classes.drawerHeader : classes.disabled
          }
        >
          <Box className={classes.statusContainer}>
            <Box
              className={!bucket.data?.length ? null : classes.countContainer}
              data-testid="counter-container"
            >
              <Typography
                className={
                  bucket.data?.length
                    ? classes.totalCountStyles
                    : classes.noData
                }
                aria-hidden="true"
              >
                {bucket.data?.length}
              </Typography>
            </Box>
            <Typography>{bucket.name}</Typography>
          </Box>
          {bucket?.data?.length && bucket?.collapsible ? (
            <MiIcon
              path={collapse ? mdiChevronUp : mdiChevronDown}
              color={theme.palette.primary.main}
              data-testid="collapsible-icon"
            />
          ) : null}
        </Box>
        <Box
          ref={setNodeRef}
          className={
            !collapse && bucket.data?.length
              ? classes.drawerContent
              : classes.noData
          }
        >
          {!collapse && children}
        </Box>
      </Box>
    </SortableContext>
  );
}
